import React, { useState, useEffect, useMemo } from 'react';
import {
  Sparkles,
  User,
  Plus,
  X,
  RotateCw,
  Check,
  Bookmark,
  Download,
  Eye,
  RefreshCw,
  Info,
  Maximize2,
  Sliders,
  ExternalLink,
} from 'lucide-react';
import {
  ClothingItem,
  BodyProfile,
  CategoryType,
  AITryOnReview,
  Outfit,
} from '../types';
import { api } from '../lib/api';

interface TryOnStudioProps {
  clothes: ClothingItem[];
  bodyProfiles: BodyProfile[];
  preselectedItem?: ClothingItem | null;
  onClearPreselected: () => void;
  onOpenBodyProfiles: () => void;
  onOutfitSaved: (outfit: Outfit) => void;
  hasGeminiKey: boolean;
}

export const TryOnStudio: React.FC<TryOnStudioProps> = ({
  clothes,
  bodyProfiles,
  preselectedItem,
  onClearPreselected,
  onOpenBodyProfiles,
  onOutfitSaved,
  hasGeminiKey,
}) => {
  // Selected garments for outfit slots
  const [selectedTop, setSelectedTop] = useState<ClothingItem | null>(null);
  const [selectedBottom, setSelectedBottom] = useState<ClothingItem | null>(null);
  const [selectedOuterwear, setSelectedOuterwear] = useState<ClothingItem | null>(null);
  const [selectedShoes, setSelectedShoes] = useState<ClothingItem | null>(null);
  const [selectedAccessory, setSelectedAccessory] = useState<ClothingItem | null>(null);

  // Active body profile
  const defaultProfile = useMemo(() => {
    return bodyProfiles.find((p) => p.is_default) || bodyProfiles[0] || null;
  }, [bodyProfiles]);

  const [activeProfile, setActiveProfile] = useState<BodyProfile | null>(defaultProfile);

  useEffect(() => {
    if (!activeProfile && defaultProfile) {
      setActiveProfile(defaultProfile);
    }
  }, [defaultProfile]);

  // Handle preselected item passed from closet view
  useEffect(() => {
    if (preselectedItem) {
      switch (preselectedItem.category) {
        case 'tops':
          setSelectedTop(preselectedItem);
          break;
        case 'bottoms':
          setSelectedBottom(preselectedItem);
          break;
        case 'outerwear':
          setSelectedOuterwear(preselectedItem);
          break;
        case 'shoes':
          setSelectedShoes(preselectedItem);
          break;
        case 'accessories':
          setSelectedAccessory(preselectedItem);
          break;
      }
      onClearPreselected();
    }
  }, [preselectedItem, onClearPreselected]);

  // Picker modal state
  const [pickerCategory, setPickerCategory] = useState<CategoryType | null>(null);
  const [pickerSearch, setPickerSearch] = useState<string>('');

  // AI Try-On generation state
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationStep, setGenerationStep] = useState<string>('');
  const [generatedTryOnImage, setGeneratedTryOnImage] = useState<string | null>(null);
  const [compareMode, setCompareMode] = useState<boolean>(false); // true = show original body, false = show AI tryon
  const [aiReview, setAiReview] = useState<AITryOnReview | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Fullscreen image preview modal
  const [isFullscreenModalOpen, setIsFullscreenModalOpen] = useState<boolean>(false);

  // Save outfit state
  const [outfitName, setOutfitName] = useState<string>('');
  const [outfitOccasion, setOutfitOccasion] = useState<string>('Casual');
  const [isSavingOutfit, setIsSavingOutfit] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // Selected items list
  const selectedItems = useMemo(() => {
    return [selectedTop, selectedBottom, selectedOuterwear, selectedShoes, selectedAccessory].filter(
      Boolean
    ) as ClothingItem[];
  }, [selectedTop, selectedBottom, selectedOuterwear, selectedShoes, selectedAccessory]);

  // Generation status text cycler during async AI inference
  useEffect(() => {
    if (!isGenerating) {
      setGenerationStep('');
      return;
    }

    const steps = [
      'Analyzing body posture, frame & lighting...',
      'Extracting cuts, fabrics & color details...',
      'Synthesizing photorealistic try-on via FLUX neural engine...',
      'Rendering natural fabric draping & realistic fit...',
      'Finalizing stylist evaluation & review...',
    ];

    let current = 0;
    setGenerationStep(steps[0]);
    const timer = setInterval(() => {
      current = (current + 1) % steps.length;
      setGenerationStep(steps[current]);
    }, 2400);

    return () => clearInterval(timer);
  }, [isGenerating]);

  // Filter clothes for picker modal
  const pickerOptions = useMemo(() => {
    if (!pickerCategory) return [];
    return clothes.filter((item) => {
      if (item.category !== pickerCategory) return false;
      if (pickerSearch.trim()) {
        const q = pickerSearch.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          item.brand?.toLowerCase().includes(q) ||
          item.color?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [clothes, pickerCategory, pickerSearch]);

  const selectItemForSlot = (item: ClothingItem) => {
    switch (item.category) {
      case 'tops':
        setSelectedTop(item);
        break;
      case 'bottoms':
        setSelectedBottom(item);
        break;
      case 'outerwear':
        setSelectedOuterwear(item);
        break;
      case 'shoes':
        setSelectedShoes(item);
        break;
      case 'accessories':
        setSelectedAccessory(item);
        break;
    }
    setPickerCategory(null);
    setPickerSearch('');
  };

  // Run AI Generative Try-On
  const handleAITryOn = async () => {
    if (!activeProfile) {
      setErrorMsg('Please upload or select a full-body photo of yourself first.');
      return;
    }

    if (selectedItems.length === 0) {
      setErrorMsg('Please select at least one garment or pair of shoes to try on.');
      return;
    }

    setIsGenerating(true);
    setErrorMsg('');

    try {
      const res = await api.ai.tryOn({
        body_image: activeProfile.image_data,
        items: selectedItems.map((it) => ({
          category: it.category,
          name: it.name,
          brand: it.brand || '',
          size: it.size || '',
          color: it.color || '',
          image_front: it.image_front,
          image_cropped: it.image_cropped,
        })),
      });

      if (res.tryon_image) {
        setGeneratedTryOnImage(res.tryon_image);
        setCompareMode(false);
      }
      setAiReview(res.review);
      if (!outfitName && res.review?.vibe_title) {
        setOutfitName(res.review.vibe_title);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Virtual try-on generation failed. Please ensure your Google AI Studio key is configured in Settings.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download generated image
  const handleDownload = () => {
    const targetImage = compareMode ? activeProfile?.image_data : generatedTryOnImage;
    if (!targetImage) return;

    const link = document.createElement('a');
    link.href = targetImage;
    link.download = `fitcheck-tryon-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save Outfit to Lookbook
  const handleSaveOutfit = async () => {
    if (selectedItems.length === 0) return;

    setIsSavingOutfit(true);
    setErrorMsg('');

    try {
      const res = await api.outfits.create({
        name: outfitName.trim() || 'My Outfit Combination',
        occasion: outfitOccasion,
        top_id: selectedTop?.id,
        bottom_id: selectedBottom?.id,
        outerwear_id: selectedOuterwear?.id,
        shoes_id: selectedShoes?.id,
        accessory_id: selectedAccessory?.id,
        body_profile_id: activeProfile?.id,
        tryon_image: generatedTryOnImage || undefined,
        ai_feedback: aiReview ? JSON.stringify(aiReview) : undefined,
        rating: aiReview ? Math.round(aiReview.fit_score / 2) : 5,
      });

      onOutfitSaved(res.outfit);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to save outfit to lookbook.');
    } finally {
      setIsSavingOutfit(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Title & Body Photo Banner */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-4 sm:p-6 backdrop-blur-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-sm">
        <div>
          <div className="flex items-center space-x-2">
            <span className="p-2 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-600 text-white shadow-md shadow-brand-500/20">
              <Sparkles className="w-5 h-5" />
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
              AI Try-On &amp; Outfit Studio
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Select pieces from your closet and send them to the AI generator to see photorealistic photos of you wearing them.
          </p>
        </div>

        {/* Active Body Photo Chip */}
        <div className="flex items-center space-x-3 bg-slate-950 p-2.5 rounded-2xl border border-slate-800 w-full md:w-auto justify-between md:justify-start">
          <div className="flex items-center space-x-2.5">
            {activeProfile ? (
              <img
                src={activeProfile.image_data}
                alt="My Body Photo"
                className="w-11 h-11 rounded-xl object-cover border border-slate-700 ring-2 ring-brand-500/30"
              />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center text-slate-500 border border-slate-800">
                <User className="w-5 h-5" />
              </div>
            )}
            <div>
              <span className="text-xs font-bold text-white block">
                {activeProfile ? activeProfile.name : 'No Body Photo'}
              </span>
              <span className="text-[11px] text-slate-400">
                {activeProfile ? 'Using for AI generation' : 'Upload your photo to try on clothes'}
              </span>
            </div>
          </div>

          <button
            onClick={onOpenBodyProfiles}
            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-brand-300 text-xs font-semibold border border-slate-700 transition-colors whitespace-nowrap"
          >
            {activeProfile ? 'Change Photo' : 'Upload Photo'}
          </button>
        </div>
      </div>

      {errorMsg && (
        <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-2xl text-xs text-red-300 flex items-center gap-2">
          <Info className="w-4 h-4 shrink-0 text-red-400" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Main Studio Work Area */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Garment Slots */}
        <div className="lg:col-span-5 space-y-3">
          <div className="flex items-center justify-between pb-1">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Outfit Pieces ({selectedItems.length}/5)
            </h3>
            {selectedItems.length > 0 && (
              <button
                onClick={() => {
                  setSelectedTop(null);
                  setSelectedBottom(null);
                  setSelectedOuterwear(null);
                  setSelectedShoes(null);
                  setSelectedAccessory(null);
                  setGeneratedTryOnImage(null);
                  setAiReview(null);
                }}
                className="text-xs text-rose-400 hover:text-rose-300 font-semibold"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Slot 1: Tops */}
          <SlotCard
            category="tops"
            label="Top (T-Shirt, Shirt, Hoodie)"
            icon="👕"
            item={selectedTop}
            onSelect={() => setPickerCategory('tops')}
            onRemove={() => setSelectedTop(null)}
          />

          {/* Slot 2: Bottoms */}
          <SlotCard
            category="bottoms"
            label="Bottoms (Jeans, Pants, Shorts)"
            icon="👖"
            item={selectedBottom}
            onSelect={() => setPickerCategory('bottoms')}
            onRemove={() => setSelectedBottom(null)}
          />

          {/* Slot 3: Outerwear */}
          <SlotCard
            category="outerwear"
            label="Outerwear (Jacket, Coat, Blazer)"
            icon="🧥"
            item={selectedOuterwear}
            onSelect={() => setPickerCategory('outerwear')}
            onRemove={() => setSelectedOuterwear(null)}
            optional
          />

          {/* Slot 4: Shoes */}
          <SlotCard
            category="shoes"
            label="Footwear (Sneakers, Boots, Loafers)"
            icon="👟"
            item={selectedShoes}
            onSelect={() => setPickerCategory('shoes')}
            onRemove={() => setSelectedShoes(null)}
          />

          {/* Slot 5: Accessory */}
          <SlotCard
            category="accessories"
            label="Accessory (Hat, Cap, Bag, Belt)"
            icon="🧢"
            item={selectedAccessory}
            onSelect={() => setPickerCategory('accessories')}
            onRemove={() => setSelectedAccessory(null)}
            optional
          />

          {/* Generate Try-On CTA Button */}
          <div className="pt-2">
            <button
              onClick={handleAITryOn}
              disabled={isGenerating || selectedItems.length === 0 || !activeProfile}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-purple-600 hover:from-brand-500 hover:to-purple-500 text-white font-extrabold text-sm shadow-xl shadow-brand-600/30 flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none"
            >
              {isGenerating ? (
                <>
                  <RotateCw className="w-5 h-5 animate-spin" />
                  <span>Synthesizing AI Try-On...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 text-brand-200" />
                  <span>Generate AI Try-On Photo</span>
                </>
              )}
            </button>
            <p className="text-[11px] text-slate-500 text-center mt-2">
              Sends your body photo + clothing to our AI engine to generate a brand new photorealistic image of you wearing this fit.
            </p>
          </div>
        </div>

        {/* Right Column: AI Try-On Viewport & AI Stylist Results */}
        <div className="lg:col-span-7 space-y-6">
          {/* Main Visual Display Card */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
            {/* Viewport Header */}
            <div className="px-5 py-3.5 border-b border-slate-800/80 bg-slate-950/70 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    generatedTryOnImage
                      ? 'bg-emerald-500 shadow-sm shadow-emerald-500/50'
                      : isGenerating
                      ? 'bg-amber-400 animate-ping'
                      : 'bg-brand-500'
                  }`}
                />
                <span className="text-xs font-bold text-white uppercase tracking-wider">
                  {generatedTryOnImage
                    ? compareMode
                      ? 'Original Body Photo'
                      : '✨ Photorealistic AI Try-On'
                    : isGenerating
                    ? 'AI Synthesis in Progress...'
                    : 'Studio Preview'}
                </span>
              </div>

              {/* Action Buttons when Generated Image exists */}
              {generatedTryOnImage && !isGenerating && (
                <div className="flex items-center space-x-1.5">
                  {/* Compare Toggle */}
                  <button
                    type="button"
                    onClick={() => setCompareMode(!compareMode)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border ${
                      compareMode
                        ? 'bg-brand-600 text-white border-brand-500 shadow-md'
                        : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
                    }`}
                    title="Toggle before / after view"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span>{compareMode ? 'Show AI Try-On' : 'Compare Original'}</span>
                  </button>

                  {/* Download */}
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition-colors"
                    title="Download high-resolution image"
                  >
                    <Download className="w-4 h-4" />
                  </button>

                  {/* Fullscreen */}
                  <button
                    type="button"
                    onClick={() => setIsFullscreenModalOpen(true)}
                    className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition-colors"
                    title="Expand full screen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>

                  {/* Re-generate */}
                  <button
                    type="button"
                    onClick={handleAITryOn}
                    disabled={isGenerating}
                    className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-brand-400 hover:text-brand-300 border border-slate-700 transition-colors"
                    title="Generate another variation"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Viewport Content */}
            <div className="relative aspect-[3/4] sm:aspect-[4/5] bg-slate-950 flex items-center justify-center p-4 overflow-hidden select-none">
              {/* STATE 1: Currently Generating */}
              {isGenerating ? (
                <div className="relative w-full h-full flex flex-col items-center justify-center text-center p-6">
                  {/* Subtle silhouette background with glowing pulsing effect */}
                  {activeProfile && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-20 filter blur-sm scale-95 pointer-events-none">
                      <img
                        src={activeProfile.image_data}
                        alt="Body"
                        className="h-full object-contain"
                      />
                    </div>
                  )}

                  <div className="relative z-10 flex flex-col items-center max-w-sm">
                    {/* Animated Pulsing Ring */}
                    <div className="relative w-20 h-20 mb-5">
                      <div className="absolute inset-0 rounded-full bg-brand-500/20 animate-ping" />
                      <div className="relative w-full h-full rounded-full bg-gradient-to-tr from-brand-600 via-indigo-600 to-purple-600 flex items-center justify-center text-white shadow-xl shadow-brand-500/30 animate-pulse">
                        <Sparkles className="w-9 h-9 animate-spin-slow" />
                      </div>
                    </div>

                    <h4 className="text-base font-bold text-white mb-2">
                      Generating Your Try-On Photo
                    </h4>
                    <p className="text-xs text-brand-300 font-medium mb-4 h-6 transition-all duration-300">
                      {generationStep}
                    </p>

                    <div className="w-48 bg-slate-800 h-1.5 rounded-full overflow-hidden mb-4">
                      <div className="bg-gradient-to-r from-brand-500 to-purple-500 h-full w-full animate-indeterminate" />
                    </div>

                    <p className="text-[11px] text-slate-500">
                      Powered by Cloudflare FLUX &amp; Google AI Multimodal Engine
                    </p>
                  </div>
                </div>
              ) : generatedTryOnImage ? (
                /* STATE 2: AI Try-On Image Generated! */
                <div className="relative h-full w-full max-w-md mx-auto flex items-center justify-center">
                  <img
                    src={compareMode && activeProfile ? activeProfile.image_data : generatedTryOnImage}
                    alt={compareMode ? 'Original Body' : 'AI Generated Try-On'}
                    className="h-full max-h-[600px] w-auto object-contain rounded-2xl shadow-2xl filter brightness-[1.01] transition-all duration-300 cursor-pointer"
                    onClick={() => setIsFullscreenModalOpen(true)}
                  />

                  {/* Non-intrusive badge */}
                  <div className="absolute bottom-3 left-3 bg-slate-950/85 backdrop-blur-md border border-slate-700/80 px-3 py-1 rounded-xl text-xs text-white flex items-center gap-1.5 shadow-lg">
                    <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                    <span className="font-bold text-brand-200">
                      {compareMode ? 'Original Photo' : 'AI Rendered Fit'}
                    </span>
                  </div>
                </div>
              ) : activeProfile ? (
                /* STATE 3: Ready to Generate (Showing Body + Selected Clothes preview) */
                <div className="w-full h-full flex flex-col sm:flex-row items-center justify-center gap-4 p-2">
                  {/* User Standing Photo */}
                  <div className="relative flex-1 h-full max-h-[520px] flex items-center justify-center">
                    <img
                      src={activeProfile.image_data}
                      alt="Your body photo"
                      className="h-full max-h-[500px] w-auto object-contain rounded-2xl drop-shadow-xl filter brightness-[0.98]"
                    />
                    <div className="absolute bottom-2 left-2 bg-slate-950/80 backdrop-blur-sm border border-slate-800 px-2 py-0.5 rounded-lg text-[10px] text-slate-300 font-medium">
                      Standing Photo
                    </div>
                  </div>

                  {/* Selected Garments Stack Preview */}
                  {selectedItems.length > 0 ? (
                    <div className="w-full sm:w-44 bg-slate-900/90 rounded-2xl p-3 border border-slate-800 flex flex-col justify-center gap-2 max-h-[480px] overflow-y-auto">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                        To Be Worn ({selectedItems.length}):
                      </span>
                      {selectedItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center space-x-2 p-1.5 rounded-xl bg-slate-950 border border-slate-800"
                        >
                          <img
                            src={item.image_cropped || item.image_front}
                            alt={item.name}
                            className="w-9 h-9 object-contain rounded-lg bg-slate-900"
                          />
                          <div className="flex-1 truncate">
                            <span className="text-[10px] font-bold text-white block truncate">
                              {item.name}
                            </span>
                            <span className="text-[9px] text-brand-400 uppercase font-semibold">
                              {item.brand || item.category}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="hidden sm:flex flex-col items-center justify-center w-44 p-4 text-center text-slate-500 text-xs border border-dashed border-slate-800 rounded-2xl">
                      Select clothing items on the left to prepare your outfit.
                    </div>
                  )}
                </div>
              ) : (
                /* STATE 4: No Body Photo Yet */
                <div className="text-center p-8 max-w-xs">
                  <div className="w-16 h-16 rounded-2xl bg-slate-900 text-slate-500 flex items-center justify-center mx-auto mb-3 border border-slate-800">
                    <User className="w-8 h-8" />
                  </div>
                  <h4 className="text-sm font-bold text-white mb-1">Add a Full-Body Photo</h4>
                  <p className="text-xs text-slate-400 mb-4">
                    Upload a standing portrait photo of yourself so the AI can generate photos of you wearing your clothes.
                  </p>
                  <button
                    onClick={onOpenBodyProfiles}
                    className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold shadow-md shadow-brand-600/30 transition-all"
                  >
                    Upload My Photo
                  </button>
                </div>
              )}
            </div>

            {/* Quick Action Footer below viewport */}
            {generatedTryOnImage && !isGenerating && (
              <div className="px-5 py-3.5 bg-slate-950/80 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-slate-300">
                  <span className="font-bold text-white">Love this fit?</span> Save it to your Lookbook with wear logs and notes.
                </div>
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById('save-outfit-section');
                      el?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="flex-1 sm:flex-initial px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-md shadow-emerald-600/20 transition-all"
                  >
                    <Bookmark className="w-3.5 h-3.5" />
                    <span>Save Look</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold border border-slate-700 flex items-center gap-1.5 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AI Stylist Analysis & Feedback Card */}
          {aiReview && (
            <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-brand-950/40 border border-brand-500/30 rounded-3xl p-5 sm:p-6 shadow-2xl space-y-5 animate-in fade-in duration-300">
              {/* Header Score & Vibe */}
              <div className="flex items-start justify-between border-b border-slate-800 pb-4">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2.5 py-1 rounded-lg bg-brand-500/20 text-brand-300 text-xs font-bold uppercase tracking-wider border border-brand-500/30">
                      AI Stylist Verdict
                    </span>
                  </div>
                  <h3 className="text-xl font-extrabold text-white mt-1.5">
                    {aiReview.vibe_title || 'Elevated Outfit'}
                  </h3>
                </div>

                {/* Score badge */}
                <div className="flex flex-col items-end">
                  <div className="flex items-baseline space-x-1 bg-brand-600/20 px-3.5 py-1.5 rounded-2xl border border-brand-500/40 shadow-inner">
                    <span className="text-2xl font-black text-brand-300">
                      {aiReview.fit_score ? aiReview.fit_score.toFixed(1) : '9.0'}
                    </span>
                    <span className="text-xs font-bold text-brand-400">/ 10</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium mt-1">Fit &amp; Harmony Score</span>
                </div>
              </div>

              {/* Critiques Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Color Harmony */}
                <div className="bg-slate-950/70 p-4 rounded-2xl border border-slate-800/90">
                  <span className="text-[11px] font-bold text-brand-400 uppercase tracking-wider block mb-1">
                    🎨 Color Palette &amp; Contrast
                  </span>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {aiReview.color_harmony}
                  </p>
                </div>

                {/* Silhouette & Proportions */}
                <div className="bg-slate-950/70 p-4 rounded-2xl border border-slate-800/90">
                  <span className="text-[11px] font-bold text-brand-400 uppercase tracking-wider block mb-1">
                    📐 Silhouette &amp; Body Frame Fit
                  </span>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {aiReview.proportions_critique}
                  </p>
                </div>
              </div>

              {/* Visual Drape Description */}
              {aiReview.drape_description && (
                <div className="bg-slate-950/70 p-4 rounded-2xl border border-slate-800/90">
                  <span className="text-[11px] font-bold text-brand-400 uppercase tracking-wider block mb-1">
                    👔 Fit &amp; Drape Breakdown on You
                  </span>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {aiReview.drape_description}
                  </p>
                </div>
              )}

              {/* Actionable Styling Tips */}
              {aiReview.styling_tips && aiReview.styling_tips.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    ✨ Pro Styling Tips for this Look
                  </span>
                  <div className="space-y-1.5">
                    {aiReview.styling_tips.map((tip, idx) => (
                      <div
                        key={idx}
                        className="flex items-start space-x-2 text-xs text-slate-300 bg-brand-500/5 p-2.5 rounded-xl border border-brand-500/10"
                      >
                        <Check className="w-3.5 h-3.5 text-brand-400 mt-0.5 shrink-0 stroke-[3]" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Occasion Tags */}
              {aiReview.occasion_match && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-slate-400 mr-1">Best suited for:</span>
                  {aiReview.occasion_match.map((occ, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 rounded-lg bg-slate-800 text-slate-200 text-xs font-semibold border border-slate-700"
                    >
                      {occ}
                    </span>
                  ))}
                </div>
              )}

              {/* Save Outfit Section */}
              <div id="save-outfit-section" className="pt-4 border-t border-slate-800 space-y-3">
                <span className="text-xs font-bold text-white block">
                  Save Outfit to Lookbook
                </span>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <input
                    type="text"
                    placeholder="Name this look (e.g. Saturday Night Streetwear)"
                    value={outfitName}
                    onChange={(e) => setOutfitName(e.target.value)}
                    className="w-full sm:flex-1 px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                  />

                  <select
                    value={outfitOccasion}
                    onChange={(e) => setOutfitOccasion(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                  >
                    <option value="Casual">Casual</option>
                    <option value="Streetwear">Streetwear</option>
                    <option value="Smart Casual">Smart Casual</option>
                    <option value="Formal">Formal</option>
                    <option value="Date Night">Date Night</option>
                    <option value="Gym / Sport">Gym / Sport</option>
                  </select>

                  <button
                    onClick={handleSaveOutfit}
                    disabled={isSavingOutfit}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2 whitespace-nowrap transition-all hover:scale-105"
                  >
                    <Bookmark className="w-4 h-4" />
                    <span>{isSavingOutfit ? 'Saving...' : 'Save to Lookbook'}</span>
                  </button>
                </div>

                {saveSuccess && (
                  <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5 animate-in fade-in">
                    <Check className="w-4 h-4" />
                    <span>Outfit saved to your Lookbook! Check it out in the Looks tab.</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Generated Image Preview Modal */}
      {isFullscreenModalOpen && generatedTryOnImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setIsFullscreenModalOpen(false)}
        >
          <div
            className="relative max-w-4xl max-h-[92vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-4 right-4 z-10 flex items-center space-x-2">
              <button
                type="button"
                onClick={handleDownload}
                className="p-2 rounded-xl bg-slate-900/90 text-white hover:bg-slate-800 border border-slate-700 shadow-lg"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => setIsFullscreenModalOpen(false)}
                className="p-2 rounded-xl bg-slate-900/90 text-white hover:bg-slate-800 border border-slate-700 shadow-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <img
              src={compareMode && activeProfile ? activeProfile.image_data : generatedTryOnImage}
              alt="High Resolution Try-On"
              className="max-h-[85vh] max-w-full object-contain rounded-2xl shadow-2xl border border-slate-800"
            />
          </div>
        </div>
      )}

      {/* Wardrobe Slot Item Picker Modal */}
      {pickerCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-200 max-h-[85vh] flex flex-col">
            {/* Picker Header */}
            <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
              <div>
                <h3 className="text-base font-bold text-white capitalize">
                  Select {pickerCategory}
                </h3>
                <p className="text-xs text-slate-400">Choose from your closet inventory</p>
              </div>
              <button
                onClick={() => {
                  setPickerCategory(null);
                  setPickerSearch('');
                }}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Picker Search */}
            <div className="p-4 border-b border-slate-800/80 bg-slate-900">
              <input
                type="text"
                placeholder={`Search ${pickerCategory} by name, brand, color...`}
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                className="w-full px-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
              />
            </div>

            {/* Garment Grid */}
            <div className="p-4 overflow-y-auto flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {pickerOptions.length === 0 ? (
                <div className="col-span-full text-center py-10 text-slate-400 text-xs">
                  No {pickerCategory} found in your closet. Add one via "Add Clothes"!
                </div>
              ) : (
                pickerOptions.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => selectItemForSlot(item)}
                    className="group bg-slate-950 border border-slate-800 hover:border-brand-500/50 rounded-2xl p-2.5 cursor-pointer transition-all hover:scale-[1.02] flex flex-col"
                  >
                    <div className="aspect-[4/5] bg-slate-900 rounded-xl overflow-hidden mb-2 relative">
                      <img
                        src={item.image_cropped || item.image_front}
                        alt={item.name}
                        className="w-full h-full object-contain p-1.5"
                      />
                      {item.brand && (
                        <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded bg-slate-900/90 text-white text-[10px] font-bold uppercase border border-white/10">
                          {item.brand}
                        </span>
                      )}
                      {item.size && (
                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-brand-500/30 text-brand-300 text-[10px] font-bold">
                          {item.size}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-bold text-slate-200 truncate group-hover:text-brand-400">
                      {item.name}
                    </span>
                    <span className="text-[10px] text-slate-400 truncate">
                      {item.color || item.subcategory}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component for individual outfit slots
interface SlotCardProps {
  category: CategoryType;
  label: string;
  icon: string;
  item: ClothingItem | null;
  onSelect: () => void;
  onRemove: () => void;
  optional?: boolean;
}

const SlotCard: React.FC<SlotCardProps> = ({
  category,
  label,
  icon,
  item,
  onSelect,
  onRemove,
  optional,
}) => {
  return (
    <div
      onClick={!item ? onSelect : undefined}
      className={`rounded-2xl border p-3 transition-all ${
        item
          ? 'bg-slate-900/90 border-brand-500/40 shadow-sm'
          : 'bg-slate-950/60 hover:bg-slate-900/60 border-slate-800/80 hover:border-slate-700 cursor-pointer border-dashed'
      }`}
    >
      {item ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 truncate">
            {/* Garment thumbnail */}
            <div className="w-14 h-14 bg-slate-950 rounded-xl overflow-hidden border border-slate-800 shrink-0 relative">
              <img
                src={item.image_cropped || item.image_front}
                alt={item.name}
                className="w-full h-full object-contain p-1"
              />
            </div>

            {/* Garment details */}
            <div className="truncate">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-xs">{icon}</span>
                {item.brand && (
                  <span className="px-2 py-0.5 rounded-md bg-slate-800 text-white text-[10px] font-extrabold uppercase tracking-wide border border-slate-700">
                    {item.brand}
                  </span>
                )}
                {item.size && (
                  <span className="px-1.5 py-0.5 rounded-md bg-brand-500/20 text-brand-300 text-[10px] font-bold">
                    {item.size}
                  </span>
                )}
              </div>
              <h4 className="text-xs font-bold text-white truncate">{item.name}</h4>
              <span className="text-[10px] text-slate-400">
                {item.color || item.subcategory}
              </span>
            </div>
          </div>

          {/* Slot Actions */}
          <div className="flex items-center space-x-1.5 ml-2">
            <button
              type="button"
              onClick={onSelect}
              className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[11px] font-medium transition-colors"
            >
              Swap
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="p-1 rounded-lg hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center space-x-2.5">
            <span className="text-lg">{icon}</span>
            <div>
              <span className="text-xs font-bold text-slate-300 block">{label}</span>
              <span className="text-[10px] text-slate-500">
                {optional ? 'Optional layer' : 'Tap to select from closet'}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-1 text-xs font-semibold text-brand-400">
            <Plus className="w-3.5 h-3.5" />
            <span>Choose</span>
          </div>
        </div>
      )}
    </div>
  );
};
